import {
  Option,
  SelectBox,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import CasesApi from "@/api/cases/index.js";

import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import styled from "styled-components";

type CaseRecord = {
  case_id: number;
  uuid?: string;
  case_number: string;
  case_ref?: string | null;
};

type CaseOption = {
  label: string;
  value: number;
  data: CaseRecord;
};

interface CaseSelectorProps {
  data?: CaseRecord[];
  searchable?: boolean;
  clearable?: boolean;
  label?: string;
  placeholder?: string;
  description?: string;
  onChange?: (value: any) => void;
  value?: any;
  [key: string]: unknown;
}

const TooltipContent = styled(
  ({ className, data }: { className?: string; data: CaseRecord }) => {
    return (
      <div className={className}>
        <div className="detail-title">Case Details</div>
        <div className="detail-item">
          <div className="detail-label">Case Number</div>
          <div className="detail-value">{data.case_number || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Case Name</div>
          <div className="detail-value">{data.case_ref || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Case UUID</div>
          <div className="detail-value">{data?.uuid || "None"}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
  }

  .detail-label {
    font-weight: bold;
    min-width: 150px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

// Render like RoleSelector via renderOption prop
const CaseOptionComponent = ({ option }: { option: Partial<CaseOption> }) => {
  const theme = useTheme();
  if (typeof option === "string") return <div>{option}</div>;
  const record = (option.data || {}) as CaseRecord;
  return (
    <div style={{ padding: "5px 0" }}>
      <div style={{ fontWeight: 500 }}>
        {record.case_number || option.label}
      </div>
      <div style={{ fontSize: "12px", color: theme.palette.text.secondary }}>
        {record.case_ref || ""}
      </div>
    </div>
  );
};

const CaseSelector: React.FC<CaseSelectorProps> = ({
  data,
  label = "Case",
  placeholder = "Select Case...",
  description = "Select the case this evidence belongs to",
  onChange,
  value,
  searchable = true,
  clearable,
  ...props
}) => {
  const [search, setSearch] = useState("");

  const query = {
    search,
    data_only: false,
    pageSize: 50,
  };

  const {
    data: infData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["cases", "list", query],
    queryFn: ({ pageParam = 1 }) => {
      return CasesApi.getCases({
        ...query,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage: any) => lastPage.nextPage,
    getPreviousPageParam: (firstPage: any) => {
      if (firstPage.page - 1 === 0) return null;
      return firstPage.page - 1;
    },
    initialPageParam: 1,
    placeholderData: (d) => d,
    enabled: !data || data.length === 0,
  });

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((infData?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  const source: CaseRecord[] =
    data && data.length > 0
      ? data
      : infData?.pages?.reduce(
          (acc: any[], page: any) => [...acc, ...page.data],
          []
        ) || [];

  // Combine number and name in label for client-side search by either
  const records: CaseOption[] = source.map((item) => ({
    label: `${item.case_number}${item.case_ref ? ` ${item.case_ref}` : ""}`,
    value: item.case_id,
    data: item,
  }));

  // Prevent forwarding ref prop down to SelectBox
  const { ref, ...cleanProps } = props as Record<string, unknown>;

  if (isLoading && (!data || data.length === 0)) {
    const defaultCase = records.find((r) => r.value === value);
    return (
      <TextInput
        size={cleanProps.size as any}
        label={label}
        placeholder={placeholder}
        description={description}
        loading={true}
        disabled={true}
        value={defaultCase?.label}
      />
    );
  }

  // Build props object to avoid TS complaining about custom props not in SelectBox types
  const commonProps = {
    ...cleanProps,
    label,
    placeholder,
    description,
    data: records,
    loading: isLoading,
    renderOption: (option: Option | string) => {
      if (typeof option === "string") {
        return <div>{option}</div>;
      } else {
        return <CaseOptionComponent option={option} />;
      }
    },
    OptionTooltip: TooltipContent,
    onChange,
    onScroll: handleScroll,
    searchFn: debouncedSearch,
    searchable,
    clearable,
    value: value !== undefined ? value : (props as any).defaultValue,
  } as any;

  return <SelectBox {...commonProps} openOnFocus={false} />;
};

export default CaseSelector;
